import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navigation from './components/shared/Navigation';
import CoachifyLanding from './components/CoachifyLanding/CoachifyLanding';
import About from './components/Coachify/About';
import Features from './components/Coachify/Features';
import Pricing from './components/Coachify/Pricing';
import Contact from './components/Coachify/Contact';
import LandingPage from './components/LandingPage/LandingPage';
import MacroCalculator from './components/MacroCalculator/MacroCalculator';
import TrainerDashboard from './components/TrainerDashboard/TrainerDashboard';
import ClientDashboard from './components/ClientDashboard/ClientDashboard';
import FeaturesComparison from './components/FeaturesComparison/FeaturesComparison';
import BookDemoButton from './components/BookDemoButton/BookDemoButton';
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import ForgotPassword from './components/Auth/ForgotPassword';
import VerifyEmail from './components/Auth/VerifyEmail';
import AcceptInvite from './components/AcceptInvite/AcceptInvite';
import CoachSettings from './components/CoachSettings/CoachSettings';
import ProtectedRoute, { PublicRoute } from './components/Auth/ProtectedRoute';
import './App.css';

// Dashboard Router - Redirects to appropriate dashboard based on role
const DashboardRouter = () => {
  const { userProfile } = useAuth();

  if (!userProfile) {
    return <Navigate to="/login" replace />;
  }

  return userProfile.role === 'trainer' ? (
    <Navigate to="/unique-physique/trainer-dashboard" replace />
  ) : (
    <Navigate to="/unique-physique/client-dashboard" replace />
  );
};

function AppRoutes() {
  const location = useLocation();
  const { currentUser } = useAuth();

  // Only show BookDemoButton on Unique Physique landing page when not authenticated
  const showBookDemoButton = location.pathname === '/unique-physique' && !currentUser;

  // Hide default Navigation on Coachify pages (they have their own header)
  const coachifyPages = ['/', '/about', '/features', '/pricing', '/contact'];
  const showNavigation = !coachifyPages.includes(location.pathname);

  return (
    <div className="App">
      {showNavigation && <Navigation />}
      <Routes>
        {/* Coachify Public Pages */}
        <Route path="/" element={<CoachifyLanding />} />
        <Route path="/about" element={<About />} />
        <Route path="/features" element={<Features />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/contact" element={<Contact />} />

        {/* Gym Client Pages */}
        <Route path="/unique-physique" element={<LandingPage />} />
        <Route path="/calculator" element={<MacroCalculator />} />
        <Route path="/gym-features" element={<FeaturesComparison />} />

        {/* Auth Routes (only accessible when NOT logged in) */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <Signup />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/accept-invite/:inviteCode" element={<AcceptInvite />} />

        {/* Protected Routes - General Dashboard Redirect */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardRouter />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - Trainer Only */}
        <Route
          path="/unique-physique/trainer-dashboard"
          element={
            <ProtectedRoute requiredRole="trainer">
              <TrainerDashboard />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - Client Only */}
        <Route
          path="/unique-physique/client-dashboard"
          element={
            <ProtectedRoute requiredRole="client">
              <ClientDashboard />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - Authenticated Users */}
        <Route
          path="/unique-physique/trainer-dashboard/settings"
          element={
            <ProtectedRoute requiredRole="trainer">
              <CoachSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/unique-physique/client-dashboard/settings"
          element={
            <ProtectedRoute requiredRole="client">
              <CoachSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/food-logger"
          element={
            <ProtectedRoute>
              <div className="coming-soon">Food Logger Coming Soon</div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/progress"
          element={
            <ProtectedRoute>
              <div className="coming-soon">Progress Tracker Coming Soon</div>
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {showBookDemoButton && <BookDemoButton />}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
